import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { updateStorage, getDefaultValue } from '../../services/storageManager'
import useAutocomplete from '@mui/material/useAutocomplete';
import Popper from '@mui/material/Popper';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { getGroupList, getAirDnaCity, getSimpleList, getFiltersList } from '../../services/front_api';

const Root = styled('div')(({ theme }) => ({
    width: '100%',
    minWidth: 0,
    position: 'relative',
    color: 'rgba(0,0,0,0.85)',
    fontSize: '14px',
    ...theme.applyStyles('dark', {
        color: 'rgba(255,255,255,0.65)',
    }),
}));

const Label = styled('label')`
  padding: 0 0 4px;
  line-height: 1.5;
  display: block;
`;

const InputWrapper = styled('div')(({ theme }) => ({
    width: '100%',
    minWidth: 0,
    border: '1px solid #d9d9d9',
    backgroundColor: '#fff',
    borderRadius: '4px',
    padding: '1px',
    display: 'flex',
    flexWrap: 'wrap',
    ...theme.applyStyles('dark', {
        borderColor: '#434343',
        backgroundColor: '#141414',
    }),
    '&:hover': {
        borderColor: '#40a9ff',
        ...theme.applyStyles('dark', {
            borderColor: '#177ddc',
        }),
    },
    '&.focused': {
        borderColor: '#40a9ff',
        boxShadow: '0 0 0 2px rgb(24 144 255 / 0.2)',
        ...theme.applyStyles('dark', {
            borderColor: '#177ddc',
        }),
    },
    '& input': {
        backgroundColor: '#fff',
        color: 'rgba(0,0,0,.85)',
        height: '30px',
        boxSizing: 'border-box',
        padding: '4px 6px',
        width: '0',
        minWidth: '30px',
        flexGrow: 1,
        border: 0,
        margin: 0,
        outline: 0,
        ...theme.applyStyles('dark', {
            color: 'rgba(255,255,255,0.65)',
            backgroundColor: '#141414',
        }),
    },
}));

function Tag(props) {
    const { label, onDelete, ...other } = props;
    return (
        <div {...other}>
            <span>{label}</span>
            <CloseIcon onClick={onDelete} />
        </div>
    );
}

Tag.propTypes = {
    label: PropTypes.string.isRequired,
    onDelete: PropTypes.func.isRequired,
};

const StyledTag = styled(Tag)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    height: '24px',
    margin: '2px',
    lineHeight: '22px',
    backgroundColor: '#fafafa',
    border: `1px solid #e8e8e8`,
    borderRadius: '2px',
    boxSizing: 'content-box',
    padding: '0 4px 0 10px',
    outline: 0,
    overflow: 'hidden',
    ...theme.applyStyles('dark', {
        backgroundColor: 'rgba(255,255,255,0.08)',
        borderColor: '#303030',
    }),
    '&:focus': {
        borderColor: '#40a9ff',
        backgroundColor: '#e6f7ff',
        ...theme.applyStyles('dark', {
            backgroundColor: '#003b57',
            borderColor: '#177ddc',
        }),
    },
    '& span': {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    '& svg': {
        fontSize: '12px',
        cursor: 'pointer',
        padding: '4px',
    },
}));

const Listbox = styled('ul')(({ theme }) => ({
    width: '100%',
    margin: 0,
    padding: 0,
    position: 'static',
    listStyle: 'none',
    backgroundColor: '#fff',
    overflow: 'auto',
    maxHeight: 'min(280px, calc(100vh - 40px))',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgb(0 0 0 / 0.15)',
    border: '1px solid #d9d9d9',
    zIndex: 10001,
    ...theme.applyStyles('dark', {
        backgroundColor: '#141414',
        borderColor: '#434343',
    }),
    '& li': {
        padding: '5px 12px',
        display: 'flex',
        '& span': {
            flexGrow: 1,
        },
        '& svg': {
            color: 'transparent',
        },
    },
    "& li[aria-selected='true']": {
        backgroundColor: '#fafafa',
        fontWeight: 600,
        ...theme.applyStyles('dark', {
            backgroundColor: '#2b2b2b',
        }),
        '& svg': {
            color: '#1890ff',
        },
    },
    [`& li.${autocompleteClasses.focused}`]: {
        backgroundColor: '#e6f7ff',
        cursor: 'pointer',
        ...theme.applyStyles('dark', {
            backgroundColor: '#003b57',
        }),
        '& svg': {
            color: 'currentColor',
        },
    },
}));

export default function MultiChoiceSearchField({
    application,
    comboApplication,
    category,
    name,
    label,
    source,
    area,
    labelField,
    valueField = 'id',
    multiple = true,
    placeholder = '',
    disabled = false
}) {
    const listApplication = comboApplication || application;
    const [options, setOptions] = useState([]);
    const [selectedValue, setSelectedValue] = useState([]);
    const [anchorEl, setInputAnchorEl] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            let data;
            try {
                if (Array.isArray(source)) {
                    data = source;
                } else {
                    switch (source) {
                        case 'group':
                            data = await getGroupList(null, area);
                            break;
                        case 'entity':
                            data = await getAirDnaCity(null, area);
                            break;
                        case 'filter':
                            data = await getFiltersList(application, area);
                            break;
                        default:
                            data = await getSimpleList(listApplication, source, labelField);
                    }
                }

                const fetchedOptions = (data || []).map(item => {
                    if (item && typeof item === 'object') {
                        const v = item.value ?? item[valueField] ?? item.id ?? item;
                        const l = item.label ?? item[labelField] ?? String(v);
                        // ensure we have value and label fields for the autocomplete
                        return { ...item, value: v, label: l };
                    }
                    return { value: item, label: String(item) };
                });

                setOptions(fetchedOptions);

                const storedValue = getDefaultValue(application, category, name);
                const storedIds = Array.isArray(storedValue)
                    ? storedValue
                    : storedValue === undefined || storedValue === null || storedValue === ''
                        ? []
                        : [storedValue];

                if (fetchedOptions.length > 0 && storedIds.length > 0) {
                    const initialObjects = fetchedOptions.filter(option => storedIds.includes(option.value));
                    setSelectedValue(multiple ? initialObjects : initialObjects.slice(0, 1));
                } else {
                    setSelectedValue([]);
                }
            } catch (error) {
                console.error('Failed to fetch data:', error);
                setOptions([]);
            }
        };

        fetchData();
    }, [source, area, application, listApplication, category, name]);

    const {
        getRootProps,
        getInputLabelProps,
        getInputProps,
        getTagProps,
        getListboxProps,
        getOptionProps,
        groupedOptions,
        value,
        focused,
        setAnchorEl,
    } = useAutocomplete({
        id: `autocomplete-${name}`,
        options: options,
        multiple: true,
        disabled,
        value: selectedValue,
        onChange: (event, newValue) => {
            const normalizedValue = multiple ? newValue : newValue.slice(-1);
            setSelectedValue(normalizedValue);
            const ids = normalizedValue.map(option => option.value);
            updateStorage(application, category, name, multiple ? ids : ids[0] ?? '');
        },
        getOptionLabel: (option) => option.label ?? option[labelField] ?? String(option.value ?? ''),
        isOptionEqualToValue: (option, value) => (option && value) ? option.value === value.value : option === value,
    });

    const handleAnchorRef = useCallback((node) => {
        setAnchorEl(node);
        setInputAnchorEl(node);
    }, [setAnchorEl]);

    return (
        <Root>
            <div {...getRootProps()}>
                <Label {...getInputLabelProps()}>{label}</Label>
                <InputWrapper ref={handleAnchorRef} className={focused ? 'focused' : ''}>
                    {value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return <StyledTag key={key} {...tagProps} label={option.label} />;
                    })}
                    <input
                        {...getInputProps()}
                        placeholder={value.length === 0 ? placeholder : ''}
                    />
                </InputWrapper>
            </div>
            {groupedOptions.length > 0 ? (
                <Popper
                    open
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    style={{
                        zIndex: 10001,
                        width: anchorEl ? anchorEl.getBoundingClientRect().width : undefined
                    }}
                    modifiers={[
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 4]
                            }
                        },
                        {
                            name: 'flip',
                            options: {
                                padding: 8
                            }
                        },
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: 'viewport',
                                padding: 8
                            }
                        }
                    ]}
                >
                    <Listbox {...getListboxProps()}>
                        {groupedOptions.map((option, index) => {
                            const { key, ...optionProps } = getOptionProps({ option, index });
                            return (
                                <li key={key} {...optionProps}>
                                    <span>{option.label}</span>
                                    <CheckIcon fontSize="small" />
                                </li>
                            );
                        })}
                    </Listbox>
                </Popper>
            ) : null}
        </Root>
    );
}

MultiChoiceSearchField.propTypes = {
    application: PropTypes.string.isRequired,
    comboApplication: PropTypes.string,
    category: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    source: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array
    ]).isRequired,
    area: PropTypes.string,
    valueField: PropTypes.string,
    multiple: PropTypes.bool,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool
};
